/**
 * Device-coverage panel on Settings → System Devices: every gap that
 * stops a trailer from being connected and online. Sol-Ark first — a linked
 * plant with no inverter row means no telemetry at all (ISS-046) — then
 * Starlink, discovery and the last telemetry run. Units with no device of a
 * kind are informational (a unit may legitimately run without solar): they are
 * shown — in a neutral header when nothing is actionable — but never counted.
 */
import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Unlink, ArrowRightLeft, Sun, Inbox, Activity, EyeOff, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getSystemIconColor } from '@/components/unit/SystemStatusIcons.helpers';
import { useDeviceIntegrity, type IntegrityIssues } from '@/hooks/useDeviceIntegrity';

/** Distinct units with no device of SOME kind — one trailer missing both counts once. */
function uncoveredCount(issues: IntegrityIssues): number {
  return new Set([...(issues.solark?.uncovered ?? []), ...issues.uncovered].map((u) => u.unit_id)).size;
}

export function DeviceIntegrityPanel() {
  const { issues, isLoading, totalIssues } = useDeviceIntegrity();
  const [expanded, setExpanded] = useState(false);

  if (isLoading || !issues) return null;
  const uncovered = uncoveredCount(issues);
  if (totalIssues === 0 && uncovered === 0) return null;

  const actionable = totalIssues > 0;
  const solark = issues.solark;
  const pending = issues.discovery?.pending ?? 0;
  const telemetry = issues.telemetry;

  return (
    <div className={cn('mb-4 border rounded-lg', actionable ? 'border-amber-500/30 bg-amber-500/[0.04]' : 'border-border bg-card/40')}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 text-left"
      >
        <div className={cn('flex items-center gap-2 text-xs font-medium', actionable ? 'text-amber-400' : 'text-muted-foreground')}>
          {actionable ? <AlertTriangle className="w-3.5 h-3.5" /> : <Info className="w-3.5 h-3.5" />}
          <span>
            {actionable
              ? `${totalIssues} device coverage ${totalIssues === 1 ? 'issue' : 'issues'}`
              : `No coverage gaps · ${uncovered} ${uncovered === 1 ? 'unit' : 'units'} with no device linked`}
          </span>
        </div>
        {expanded
          ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
          : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
        }
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3">
          {solark && solark.plants_without_inverter.length > 0 && (
            <Section icon={Sun} title={`Linked plant, no inverter row — no telemetry (${solark.plants_without_inverter.length})`}>
              {solark.plants_without_inverter.map((p) => (
                <Row key={p.plant_id} primary={p.unit_code} note={p.plant_name && p.plant_name !== p.unit_code ? `plant "${p.plant_name}"` : 'heals on the next discover run'} mono={p.external_plant_id} />
              ))}
            </Section>
          )}

          {solark && (solark.link_conflicts?.length ?? 0) > 0 && (
            <Section icon={ArrowRightLeft} title={`Plant and inverter linked to different units (${solark.link_conflicts!.length})`}>
              {solark.link_conflicts!.map((c) => (
                <Row key={c.plant_id} primary={c.unit_code} note={`plant linked here · inverter row linked to ${c.inverter_unit_code ?? 'another unit'}`} mono={c.external_plant_id} />
              ))}
            </Section>
          )}

          {solark && solark.never_reported.length > 0 && (
            <Section icon={EyeOff} title={`Linked, never reported (${solark.never_reported.length})`}>
              {solark.never_reported.map((i) => (
                <Row key={i.inverter_id} primary={i.unit_code ?? i.external_inverter_id} note="no telemetry received yet" mono={i.external_inverter_id} />
              ))}
            </Section>
          )}

          {solark && solark.mismatches.length > 0 && (
            <Section icon={ArrowRightLeft} title={`Plant name / unit mismatch (${solark.mismatches.length})`}>
              {solark.mismatches.map((p) => (
                <Row key={p.plant_id} primary={p.plant_name ?? '(unnamed)'} note={`linked to ${p.unit_code}`} mono={p.external_plant_id} />
              ))}
            </Section>
          )}

          {solark && (solark.unlinked_plants.length > 0 || solark.unlinked_inverters.length > 0) && (
            <Section icon={Unlink} title={`Unlinked Sol-Ark (${solark.unlinked_plants.length + solark.unlinked_inverters.length})`}>
              {solark.unlinked_plants.map((p) => (
                <Row key={p.plant_id} primary={p.plant_name ?? '(unnamed plant)'} note="plant · no unit linked" mono={p.external_plant_id} />
              ))}
              {solark.unlinked_inverters.map((i) => (
                <Row key={i.inverter_id} primary={i.external_inverter_id} note="inverter row · no unit linked" mono="" />
              ))}
            </Section>
          )}

          {issues.mismatches.length > 0 && (
            <Section icon={ArrowRightLeft} title={`Nickname / Unit Mismatch (${issues.mismatches.length})`}>
              {issues.mismatches.map((m) => (
                <Row key={m.terminal_id} dotId={m.terminal_id} dot={m.connection_status} primary={m.nickname} note={`linked to ${m.unit_code}`} mono={m.service_line_number} />
              ))}
            </Section>
          )}

          {issues.unlinked.length > 0 && (
            <Section icon={Unlink} title={`Unlinked Terminals (${issues.unlinked.length})`}>
              {issues.unlinked.map((u) => (
                <Row key={u.terminal_id} dotId={u.terminal_id} dot={u.connection_status} primary={u.nickname} note={`${u.is_active ? 'active' : 'inactive'} · no unit linked`} mono={u.service_line_number} />
              ))}
            </Section>
          )}

          {pending > 0 && (
            <Section icon={Inbox} title={`${pending} device${pending === 1 ? '' : 's'} waiting in New Devices`} />
          )}

          {telemetry && telemetry.short_by > 0 && (
            <Section icon={Activity} title={`Telemetry updated ${telemetry.items_success} of ${telemetry.linked_inverters} linked inverters on the last run`} />
          )}

          <Uncovered issues={issues} />
        </div>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, children }: { icon: typeof Sun; title: string; children?: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold text-amber-400/80 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
        <Icon className="w-3 h-3" />
        {title}
      </div>
      {children && <div className="space-y-1">{children}</div>}
    </div>
  );
}

function Row({ dot, dotId, primary, note, mono }: { dot?: string; dotId?: string; primary: string; note: string; mono: string }) {
  return (
    <div className="flex items-center gap-3 px-2 py-1.5 rounded bg-card/50 border border-border/30 text-xs">
      {dot !== undefined && <StatusDot status={dot} id={dotId} />}
      <span className="text-foreground font-medium">{primary}</span>
      <span className="text-muted-foreground">{note}</span>
      {mono && <span className="text-[10px] text-muted-foreground/60 font-mono ml-auto">{mono}</span>}
    </div>
  );
}

/** Informational, not counted: which units have no device of a kind at all. */
function Uncovered({ issues }: { issues: IntegrityIssues }) {
  const noSolark = issues.solark?.uncovered.length ?? 0;
  const noStarlink = issues.uncovered.length;
  if (noSolark === 0 && noStarlink === 0) return null;
  return (
    <div className="text-[11px] text-muted-foreground pt-1 border-t border-border/30">
      Units with no device linked — Without Sol-Ark: {noSolark} · without Starlink: {noStarlink}
      {noSolark > 0 && issues.solark && (
        <span className="ml-1 font-mono text-muted-foreground/70">({issues.solark.uncovered.map((u) => u.unit_code).join(', ')})</span>
      )}
    </div>
  );
}

/** Starlink connection dot, coloured by the shared system-status palette (online = lit). */
function StatusDot({ status, id }: { status: string; id?: string }) {
  return (
    <span
      data-testid={id ? `status-dot-${id}` : undefined}
      className={cn('w-1.5 h-1.5 rounded-full shrink-0 bg-current', getSystemIconColor('starlink', status === 'online'))}
      aria-label={status}
    />
  );
}
